// src/Components/ProjectOwnersAdminsPanel/ProjectOwnersAdminsPanel.tsx
//
// DEV-1039. Project-scope governance grants UI — the "Owners & Admins" section
// on the Project Access tab. Replaces the V1 ACL editor (deleted in DEV-1040)
// and inherits the people-direct grant model from CommunityPermissionsV2Panel.
//
// Bundles surfaced here are filtered to grant_mode='user' per DEV-1036:
//   Owner, Admin, Viewer, Contributor.
// Operational bundles (slot-tied: Editor, Risk Manager, External Collaborator)
// do NOT appear here — those are granted to role slots on the Teams tab.
//
// UX pattern (locked in DEV-1039 mockup session):
//   - Row list stays calm
//   - Add/Edit slides in via <Tray>
//   - Self-protection: current user's row has no edit affordance (V1 parity)
//   - Inline revoke confirmation within the edit tray
//   - Empty / loading / error / readonly states all explicit

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { permissionsApiV2 } from "../../security/v2/apiClient";
import type {
  PermissionGrantDto,
  PermissionSetDto,
} from "../../security/v2/types";
import { fetchAllSecurityUsers, type SecurityUserResponse } from "../../api/security";
import { useApiImpactSignature } from "../../api/useApiImpactSignature";
import { PLATFORM_PERMISSION_SET_GUIDS } from "../../security/v2/types";
import { normalizeGuid } from "../../utils/guid";
import { Tray } from "../Tray/Tray";
import {
  UserIdentityCard,
  resolveHomeCommunityName,
  computeDefaultInviteMessage,
} from "../IdentityCard/IdentityCard";
import styles from "./ProjectOwnersAdminsPanel.module.css";

interface Props {
  projectGuid: string;
  /** Project name — surfaced in the section header ("Owners & Admins for ...") if provided. */
  projectName?: string;
  /** Parent guards admin-only mutation visibility; pass through for an extra check. Default true. */
  canAdmin?: boolean;
  /**
   * The acting user's GUID. Used for self-protection (no edit on own row) AND
   * as `created_by` on the workflow request emitted when granting a bundle to
   * someone else. Falls back to empty string if not supplied.
   */
  currentUserGuid?: string;
  /**
   * DEV-1039 iteration. Map of community entity GUID (uppercase) → display name.
   * Used to resolve the selected user's home community (primaryLocationEntityGuid)
   * to a human-readable name in the identity preview card. The parent builds
   * this from apiCommunityData.entities. When undefined or missing for a given
   * GUID, the card falls back to "(no home community)".
   */
  communityNameByGuid?: Map<string, string>;
  /**
   * DEV-1039 iter 3. Pre-resolved project owner display info, used to render
   * the "Owner: {name} · set in Basics" info line at the top of the Admins
   * section. Section content itself is purely admin-level grants — owner
   * authority is derived from project.ownerGuid in Basics, NOT from an
   * explicit Owner-bundle grant in permission_grants. This line keeps the
   * owner visible without confusing the data model.
   */
  projectOwner?: {
    name: string;
    /** "user" or "community" — affects the label suffix. */
    kind: "user" | "community";
  };
}

interface LoadState {
  loading: boolean;
  error: string | null;
  bundles: PermissionSetDto[];
  grants: PermissionGrantDto[];
  users: SecurityUserResponse[];
}

const initialState: LoadState = {
  loading: true,
  error: null,
  bundles: [],
  grants: [],
  users: [],
};

type TrayMode = "add" | "edit";

interface TrayCtx {
  mode: TrayMode;
  grant?: PermissionGrantDto;
}

export default function ProjectOwnersAdminsPanel({
  projectGuid,
  projectName,
  canAdmin = true,
  currentUserGuid = "",
  communityNameByGuid,
  projectOwner,
}: Props) {
  const { t } = useAppTranslation("projectConfig");
  const [state, setState] = useState<LoadState>(initialState);
  const [busy, setBusy] = useState(false);

  // Tray state
  const [trayCtx, setTrayCtx] = useState<TrayCtx | null>(null);

  // Form state inside the tray
  const [pickUserGuid, setPickUserGuid] = useState<string>("");
  const [pickBundleGuid, setPickBundleGuid] = useState<string>("");
  const [userSearch, setUserSearch] = useState("");
  const [userPickerOpen, setUserPickerOpen] = useState(false);
  const [inviteMessage, setInviteMessage] = useState("");
  /**
   * DEV-1039 iter 2. Tracks whether the admin has manually edited the
   * invitation textarea. Default behavior: when false, the templated default
   * message flows in live as the user/bundle/sender change. Flips to true on
   * any onChange (typing OR clearing) so admin edits are never overwritten.
   * Reset to false by openAddTray() and by the "Reset to default" link.
   */
  const [messageUserEdited, setMessageUserEdited] = useState(false);
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);

  // DEV-1039 iteration. Impact summary for the currently-selected user.
  // The hook handles its own fetch + caching (SignalR-aware via the shared
  // queryCache). Passing null when no user is picked makes it inert.
  const {
    tiers: userImpactTiers,
    loading: userImpactLoading,
  } = useApiImpactSignature(pickUserGuid || null);

  const load = useCallback(async () => {
    if (!projectGuid) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const [bundles, grants, users] = await Promise.all([
        permissionsApiV2.listPermissionSetsForScope("project", projectGuid),
        permissionsApiV2.listGrantsForScope("project", projectGuid),
        fetchAllSecurityUsers(),
      ]);
      // DEV-1036: filter to governance bundles only.
      // DEV-1039 iter 3. Governance bundles only, EXCLUDING Owner.
      // Owner is set exclusively via the Basics tab (project.ownerGuid) —
      // it's a single-instance role. Surfacing "Owner" in this picker would
      // create a second path to assign it, breaking the singleton invariant.
      const ownerGuidNorm =
        PLATFORM_PERMISSION_SET_GUIDS.Owner.toUpperCase();
      const governanceBundles = bundles.filter(
        (b) =>
          b.grantMode === "user" &&
          normalizeGuid(b.permissionSetGuid) !== ownerGuidNorm,
      );
      // Filter grants to user-direct ones (skip slot grants — those live on Teams tab).
      const userGrants = grants.filter(
        (g) => g.userGuid && g.userGuid !== "00000000-0000-0000-0000-000000000000"
      );
      setState({
        loading: false,
        error: null,
        bundles: governanceBundles,
        grants: userGrants,
        users: users.filter((u) => u.isActive),
      });
    } catch (err: any) {
      setState((s) => ({
        ...s,
        loading: false,
        error: String(err?.message ?? err),
      }));
    }
  }, [projectGuid]);

  useEffect(() => { load(); }, [load]);

  /**
   * DEV-1050 follow-up — refetch on SecurityUpdated SignalR events so
   * the grant list reflects accept/decline actions taken by recipients
   * (or revokes by other admins) without requiring a manual reload.
   * The custom event is dispatched by signalrConnection.ts when the
   * backend pushes KpiHub.Events.SecurityUpdated; we listen project-
   * wide rather than filtering by projectGuid because the message
   * payload carries the GUID we already match on via the load() refetch.
   */
  useEffect(() => {
    const handleSecurityUpdate = () => { load(); };
    window.addEventListener("l4e_security_updated", handleSecurityUpdate);
    return () => {
      window.removeEventListener("l4e_security_updated", handleSecurityUpdate);
    };
  }, [load]);

  // ── Lookups ──────────────────────────────────────────────────

  const userByGuid = useMemo(() => {
    const m = new Map<string, SecurityUserResponse>();
    for (const u of state.users) m.set(normalizeGuid(u.userGuid), u);
    return m;
  }, [state.users]);

  const bundleByGuid = useMemo(() => {
    const m = new Map<string, PermissionSetDto>();
    for (const b of state.bundles) m.set(normalizeGuid(b.permissionSetGuid), b);
    return m;
  }, [state.bundles]);

  // GUIDs of users who already hold a grant — excluded from the Add typeahead.
  const grantedUserGuids = useMemo(() => {
    const s = new Set<string>();
    for (const g of state.grants) {
      if (g.userGuid) s.add(normalizeGuid(g.userGuid));
    }
    return s;
  }, [state.grants]);

  // Typeahead pool: active users NOT already granted. Excluded from the picker
  // in the Add tray to prevent duplicate-grant attempts.
  const userOptions = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    const pool = state.users.filter(
      (u) => !grantedUserGuids.has(normalizeGuid(u.userGuid))
    );
    const filtered = q
      ? pool.filter(
          (u) =>
            (u.displayName ?? u.name ?? "").toLowerCase().includes(q) ||
            (u.email ?? "").toLowerCase().includes(q)
        )
      : pool;
    return filtered
      .slice()
      .sort((a, b) =>
        (a.displayName || a.name || a.email || "").localeCompare(
          b.displayName || b.name || b.email || "",
          undefined,
          { numeric: true, sensitivity: "base" }
        )
      )
      .slice(0, 50);
  }, [state.users, userSearch, grantedUserGuids]);

  const currentUserGuidNorm = normalizeGuid(currentUserGuid);

  // ── Tray lifecycle ───────────────────────────────────────────

  const openAddTray = useCallback(() => {
    setPickUserGuid("");
    setPickBundleGuid("");
    setUserSearch("");
    setInviteMessage("");
    setMessageUserEdited(false);
    setConfirmingRevoke(false);
    setTrayCtx({ mode: "add" });
  }, []);

  const openEditTray = useCallback((g: PermissionGrantDto) => {
    setPickUserGuid(g.userGuid ?? "");
    setPickBundleGuid(g.permissionSetGuid);
    setUserSearch("");
    setInviteMessage("");
    setConfirmingRevoke(false);
    setTrayCtx({ mode: "edit", grant: g });
  }, []);

  const closeTray = useCallback(() => {
    setTrayCtx(null);
    setConfirmingRevoke(false);
  }, []);

  // ── Mutations ────────────────────────────────────────────────

  const handleAssign = useCallback(async () => {
    if (!canAdmin || !pickUserGuid || !pickBundleGuid) return;
    setBusy(true);
    try {
      const grant = await permissionsApiV2.createGrantForScope(
        "project",
        projectGuid,
        {
          permissionSetGuid: pickBundleGuid,
          userGuid: pickUserGuid,
        }
      );

      // Workflow emit for non-self assignments. Mirrors the
      // CommunityPermissionsV2Panel pattern but with project target.
      if (grant.status === "pending") {
        try {
          const { addOrUpdateWorkflow, submitRequest } = await import("../../workflow");
          const userName =
            userByGuid.get(normalizeGuid(pickUserGuid))?.displayName ??
            userByGuid.get(normalizeGuid(pickUserGuid))?.name ??
            "User";
          const bundleName =
            bundleByGuid.get(normalizeGuid(pickBundleGuid))?.name ?? "Role";
          const now = new Date();
          const trimmedMessage = inviteMessage.trim();
          const makeGuid = (): string => {
            try {
              const c: any = (globalThis as any).crypto;
              if (c?.randomUUID) return String(c.randomUUID());
            } catch { /* fall through */ }
            return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
              const r = (Math.random() * 16) | 0;
              const v = c === "x" ? r : (r & 0x3) | 0x8;
              return v.toString(16);
            });
          };
          const draft = {
            workflow_guid: makeGuid(),
            target_type: "project" as const,
            target_guid: projectGuid,
            action_type: "project.role_assignment",
            title: `${bundleName} invitation`,
            description: trimmedMessage
              ? `${trimmedMessage}\n\n— You've been invited to take the ${bundleName} role on this project.`
              : `You've been invited to take the ${bundleName} role on this project.`,
            payload: {
              project_guid: projectGuid,
              user_guid: pickUserGuid,
              permission_set_guid: pickBundleGuid,
              grant_guid: grant.grantGuid,
              role: bundleName,
              user_display_name: userName,
              custom_message: trimmedMessage,
              // DEV-1050 follow-up — surface the bundle's 2FA requirement
              // so the invitee's Action Center card can warn them that the
              // role will be dormant until they enroll TOTP. Captured at
              // workflow-creation time so the consumer doesn't have to
              // re-fetch the bundle when rendering the card.
              bundle_requires_2fa:
                bundleByGuid.get(normalizeGuid(pickBundleGuid))?.requires2fa === true,
            },
            status: "draft" as const,
            version: 1,
            created_by: String(currentUserGuid || ""),
            created_at: now.toISOString(),
            updated_at: now.toISOString(),
            assigned_to_guids: [pickUserGuid],
            history: [{ action: "draft" as const, by: String(currentUserGuid || ""), at: now.toISOString() }],
          };
          const submitted = submitRequest(draft as any, String(currentUserGuid || ""));
          addOrUpdateWorkflow(submitted);
        } catch (wfErr) {
          console.warn("[ProjectOwnersAdminsPanel] workflow emit failed:", wfErr);
        }
      }

      closeTray();
      await load();
    } catch (err: any) {
      setState((s) => ({ ...s, error: String(err?.message ?? err) }));
    } finally {
      setBusy(false);
    }
  }, [
    canAdmin,
    pickUserGuid,
    pickBundleGuid,
    projectGuid,
    userByGuid,
    bundleByGuid,
    inviteMessage,
    currentUserGuid,
    load,
    closeTray,
  ]);

  // Edit: change bundle on an existing grant. V2 doesn't have a bundle-swap
  // endpoint; revoke + create is the idempotent path.
  const handleUpdateBundle = useCallback(async () => {
    if (!canAdmin || !trayCtx?.grant || !pickBundleGuid) return;
    const current = trayCtx.grant;
    if (current.permissionSetGuid.toUpperCase() === pickBundleGuid.toUpperCase()) {
      // No-op — bundle unchanged.
      closeTray();
      return;
    }
    setBusy(true);
    try {
      await permissionsApiV2.revokeGrant(current.grantGuid);
      await permissionsApiV2.createGrantForScope("project", projectGuid, {
        permissionSetGuid: pickBundleGuid,
        userGuid: current.userGuid ?? null,
      });
      closeTray();
      await load();
    } catch (err: any) {
      setState((s) => ({ ...s, error: String(err?.message ?? err) }));
    } finally {
      setBusy(false);
    }
  }, [canAdmin, trayCtx, pickBundleGuid, projectGuid, load, closeTray]);

  const handleRevoke = useCallback(async () => {
    if (!canAdmin || !trayCtx?.grant) return;
    setBusy(true);
    try {
      await permissionsApiV2.revokeGrant(trayCtx.grant.grantGuid);
      closeTray();
      await load();
    } catch (err: any) {
      setState((s) => ({ ...s, error: String(err?.message ?? err) }));
    } finally {
      setBusy(false);
    }
  }, [canAdmin, trayCtx, load, closeTray]);

  // ── Derived values for current tray context ──────────────────

  const selectedUser = pickUserGuid
    ? userByGuid.get(normalizeGuid(pickUserGuid)) ?? null
    : null;
  const selectedBundle = pickBundleGuid
    ? bundleByGuid.get(normalizeGuid(pickBundleGuid)) ?? null
    : null;
  const isSelfAssignment = pickUserGuid && normalizeGuid(pickUserGuid) === currentUserGuidNorm;

  // DEV-1039 iteration. 2FA cross-check between selected bundle and user.
  // Data path: SecurityUser.IsTwoFactorEnabled (already exposed end-to-end —
  // no new endpoint). When both conditions are true, the grant is created
  // but stays dormant until the user enrolls TOTP. We surface that ahead of
  // time so admins know what they're committing to.
  const userHasTwoFa = selectedUser?.isTwoFactorEnabled === true;
  const bundleRequiresTwoFa = selectedBundle?.requires2fa === true;
  const grantWillBeDormant = !!selectedUser && bundleRequiresTwoFa && !userHasTwoFa;
  const grantWillActivateImmediately = !!selectedUser && bundleRequiresTwoFa && userHasTwoFa;

  // DEV-1039 iter 2. Resolve the sender (current acting user) so the templated
  // default invitation message can sign off with their display name. Empty
  // when the sender's record is missing — the template still renders with
  // an empty signature rather than blocking.
  const senderUser = currentUserGuid
    ? userByGuid.get(normalizeGuid(currentUserGuid)) ?? null
    : null;

  // DEV-1039 iter 2. Default invitation message — computed from current
  // selections. Empty until both a user and bundle are picked.
  const defaultInviteMessage = useMemo(
    () => computeDefaultInviteMessage({
      recipient: selectedUser,
      roleName: selectedBundle?.name ?? "",
      projectName,
      sender: senderUser,
      t,
      templateKey: "access.oa.messageTemplate",
      fallbackProjectNameKey: "access.oa.thisProject",
    }),
    [selectedUser, selectedBundle, projectName, senderUser, t],
  );

  // DEV-1039 iter 2. When the admin hasn't edited the message, let the
  // computed default flow through live as fields change. Once they edit
  // (or click "Reset to default"), this effect is inert until the next
  // tray open or reset.
  useEffect(() => {
    if (messageUserEdited) return;
    if (trayCtx?.mode !== "add") return;
    setInviteMessage(defaultInviteMessage);
  }, [defaultInviteMessage, messageUserEdited, trayCtx?.mode]);

  const handleResetMessage = useCallback(() => {
    setInviteMessage(defaultInviteMessage);
    setMessageUserEdited(false);
  }, [defaultInviteMessage]);

  // ── Render helpers ───────────────────────────────────────────

  const renderRow = (g: PermissionGrantDto) => {
    const u = userByGuid.get(normalizeGuid(g.userGuid ?? ""));
    const b = bundleByGuid.get(normalizeGuid(g.permissionSetGuid));
    const userLabel = u
      ? u.displayName || u.name || "(no name)"
      : g.userGuid ?? "(unknown)";
    const emailLabel = u?.email ?? "";
    const bundleLabel = b?.name ?? "(unknown bundle)";
    const isSelf = normalizeGuid(g.userGuid ?? "") === currentUserGuidNorm;
    const requires2fa = b?.requires2fa === true;

    return (
      <div key={g.grantGuid} className={styles.row}>
        <div className={styles.rowMain}>
          <div className={styles.rowTitle}>
            {userLabel}
            <span className={styles.bundlePill}>{bundleLabel}</span>
            {requires2fa && (
              <span
                className={styles.pill2fa}
                title={t("access.oa.pill2faTooltip")}
              >
                {t("access.oa.pill2fa")}
              </span>
            )}
            {g.status === "pending" && (
              <span
                className={styles.pillPending}
                title={t("access.oa.pillPendingTooltip")}
              >
                {t("access.oa.pillPending")}
              </span>
            )}
            {g.status === "declined" && (
              <span
                className={styles.pillDeclined}
                title={t("access.oa.pillDeclinedTooltip")}
              >
                {t("access.oa.pillDeclined")}
              </span>
            )}
            {isSelf && (
              <span className={styles.pillSelf}>{t("access.oa.pillSelf")}</span>
            )}
          </div>
          {emailLabel && (
            <div className={styles.rowSubtitle}>{emailLabel}</div>
          )}
        </div>
        <div className={styles.rowActions}>
          {isSelf ? (
            <button
              type="button"
              className={styles.btnRow}
              disabled
              title={t("access.oa.protectedTooltip")}
            >
              {t("access.oa.protected")}
            </button>
          ) : canAdmin ? (
            <button
              type="button"
              className={styles.btnRow}
              onClick={() => openEditTray(g)}
            >
              {t("access.oa.edit")}
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  // ── Render ───────────────────────────────────────────────────

  return (
    <>
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}>
            {projectName
              ? t("access.oa.titleNamed", { name: projectName })
              : t("access.oa.title")}
            {state.grants.length > 0 && (
              <span className={styles.count}>({state.grants.length})</span>
            )}
          </div>
          {canAdmin && !state.loading && (
            <button
              type="button"
              className={styles.btnAdd}
              onClick={openAddTray}
            >
              + {t("access.oa.addPerson")}
            </button>
          )}
        </div>

        <div className={styles.sectionBody}>
          {/* DEV-1039 iter 3. Owner info line. Owner is set via the Basics
              tab (project.ownerGuid) — NOT through this section's bundle
              grants. Surfacing it here gives admins one place to see all
              elevated access on the project. */}
          {projectOwner && (
            <div className={styles.ownerInfoLine}>
              <span className={styles.ownerInfoLabel}>
                {t("access.oa.ownerLabel")}
              </span>{" "}
              <span className={styles.ownerInfoName}>
                {projectOwner.name}
              </span>
              {projectOwner.kind === "community" && (
                <span className={styles.ownerInfoKind}>
                  {" · "}{t("access.oa.ownerKindCommunity")}
                </span>
              )}
              <span className={styles.ownerInfoSetIn}>
                {" · "}{t("access.oa.ownerSetInBasics")}
              </span>
            </div>
          )}

          {!canAdmin && (
            <div className={styles.readonlyBanner}>
              {t("access.oa.readonlyBanner")}
            </div>
          )}

          {state.error && (
            <div className={styles.errorBanner}>
              <span>{t("access.oa.errorBanner")}</span>
              <button
                type="button"
                onClick={load}
                className={styles.btnRetry}
              >
                {t("access.oa.retry")}
              </button>
            </div>
          )}

          {state.loading ? (
            <div className={styles.list}>
              <div className={styles.skeletonRow} />
              <div className={styles.skeletonRow} />
              <div className={styles.skeletonRow} />
            </div>
          ) : state.grants.length === 0 && !state.error ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyTitle}>{t("access.oa.emptyTitle")}</div>
              <div className={styles.emptyHelp}>{t("access.oa.emptyHelp")}</div>
            </div>
          ) : (
            <div className={styles.list}>
              {state.grants.map(renderRow)}
            </div>
          )}
        </div>
      </div>

      {/* ── Add / Edit Tray ────────────────────────────────────── */}

      <Tray
        open={trayCtx !== null}
        onClose={closeTray}
        title={
          trayCtx?.mode === "add"
            ? t("access.oa.trayAddTitle")
            : trayCtx?.mode === "edit"
              ? t("access.oa.trayEditTitle", {
                  name:
                    userByGuid.get(normalizeGuid(trayCtx.grant?.userGuid ?? ""))
                      ?.displayName ?? "this user",
                })
              : undefined
        }
        width={460}
      >
        <Tray.Body className={styles.trayBody}>
          {/* DEV-1039 iteration. Bundle picker FIRST — admins pick the role,
              THEN pick the person. Reverse of an "edit person, change their
              bundle" mental model, but right for an "I need an admin" workflow. */}
          <div className={styles.field}>
            <label className={styles.fieldLabel}>
              {t("access.oa.fieldBundle")}
            </label>
            <select
              className={styles.input}
              value={pickBundleGuid}
              onChange={(e) => setPickBundleGuid(e.target.value)}
              disabled={busy}
            >
              <option value="">{t("access.oa.chooseBundle")}</option>
              {state.bundles.map((b) => (
                <option key={b.permissionSetGuid} value={b.permissionSetGuid}>
                  {b.name}
                  {b.requires2fa ? ` · ${t("access.oa.bundleRequires2fa")}` : ""}
                </option>
              ))}
            </select>
            {selectedBundle?.description && (
              <div className={styles.bundleHint}>{selectedBundle.description}</div>
            )}
            {/* DEV-1039 iteration. 2FA cross-check messaging — appears only
                when the bundle requires 2FA AND a user is selected. Two
                branches: dormant warning (yellow) when user lacks TOTP;
                immediate-activation note (neutral) when user has it. */}
            {grantWillBeDormant && (
              <div className={styles.bundleWarn2fa}>
                {t("access.oa.dormantWarning", {
                  name: selectedUser?.displayName || selectedUser?.name || "",
                })}
              </div>
            )}
            {grantWillActivateImmediately && (
              <div className={styles.bundleInfo2fa}>
                {t("access.oa.willActivateImmediately", {
                  name: selectedUser?.displayName || selectedUser?.name || "",
                })}
              </div>
            )}
          </div>

          {/* ── User picker / identity card ──
              No user selected (add mode): typeahead.
              User selected: rich identity preview card with avatar +
              home community + impact radar so the admin can confirm identity
              before granting authority. */}
          <div className={styles.field}>
            <label className={styles.fieldLabel}>{t("access.oa.fieldUser")}</label>
            {selectedUser ? (
              <UserIdentityCard
                user={selectedUser}
                homeCommunityName={resolveHomeCommunityName(selectedUser, communityNameByGuid)}
                tiers={userImpactTiers}
                loading={userImpactLoading}
                showNo2faPill={grantWillBeDormant}
                onClear={
                  trayCtx?.mode === "add"
                    ? () => { setPickUserGuid(""); setUserSearch(""); }
                    : undefined
                }
                pillNo2faKey="access.oa.pillNo2fa"
                pillNo2faTooltipKey="access.oa.pillNo2faTooltip"
                changePersonKey="access.oa.changePerson"
                homeCommunityLineKey="access.oa.homeCommunityLine"
                noHomeCommunityKey="access.oa.noHomeCommunity"
                impactLoadingKey="access.oa.impactLoading"
                impactUnavailableKey="access.oa.impactUnavailable"
                t={t}
              />
            ) : (
              <div className={styles.typeahead}>
                <input
                  className={styles.input}
                  type="text"
                  value={userSearch}
                  onChange={(e) => { setUserSearch(e.target.value); setUserPickerOpen(true); }}
                  onFocus={() => setUserPickerOpen(true)}
                  onBlur={() => setTimeout(() => setUserPickerOpen(false), 150)}
                  placeholder={t("access.oa.userSearchPlaceholder")}
                  disabled={busy}
                />
                {userPickerOpen && userSearch.trim() !== "" && (
                  <div className={styles.typeaheadResults}>
                    {userOptions.length === 0 ? (
                      <div className={styles.typeaheadEmpty}>
                        {t("access.oa.noUsersMatch")}
                      </div>
                    ) : (
                      userOptions.map((u) => (
                        <div
                          key={u.userGuid}
                          className={styles.typeaheadItem}
                          onMouseDown={() => {
                            setPickUserGuid(u.userGuid);
                            setUserSearch("");
                            setUserPickerOpen(false);
                          }}
                        >
                          <div className={styles.typeaheadName}>
                            {u.displayName || u.name || "(no name)"}
                          </div>
                          {u.email && (
                            <div className={styles.typeaheadEmail}>{u.email}</div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Templated message — only for non-self assignments in add mode.
              DEV-1039 iter 2. Default populates live from selected user/bundle/
              sender/project until the admin types; after that it preserves
              their edits. "Reset to default" link appears only when edits
              diverge from the current computed default. */}
          {trayCtx?.mode === "add" && pickUserGuid && !isSelfAssignment && (
            <div className={styles.field}>
              <label className={styles.fieldLabel}>
                {t("access.oa.fieldMessage")}
                <span className={styles.fieldSub}>
                  {t("access.oa.fieldMessageSub")}
                </span>
              </label>
              <textarea
                className={styles.textarea}
                value={inviteMessage}
                onChange={(e) => {
                  setInviteMessage(e.target.value);
                  // Flip the lock on first edit (typing OR clearing). From here
                  // forward, this textarea is fully admin-controlled until
                  // they reset or close the tray.
                  if (!messageUserEdited) setMessageUserEdited(true);
                }}
                placeholder={t("access.oa.messagePlaceholder")}
                disabled={busy}
                rows={4}
                maxLength={500}
              />
              <div className={styles.messageFooter}>
                {messageUserEdited && inviteMessage !== defaultInviteMessage && defaultInviteMessage !== "" ? (
                  <button
                    type="button"
                    className={styles.btnResetMessage}
                    onClick={handleResetMessage}
                    disabled={busy}
                  >
                    {t("access.oa.resetMessage")}
                  </button>
                ) : (
                  <span />
                )}
                <div className={styles.charCount}>{inviteMessage.length} / 500</div>
              </div>
            </div>
          )}

          {/* ── Revoke confirmation (edit mode, inline) ── */}
          {trayCtx?.mode === "edit" && confirmingRevoke && (
            <div className={styles.revokeConfirm}>
              <div className={styles.revokeConfirmText}>
                {t("access.oa.revokeConfirmText", {
                  name:
                    userByGuid.get(normalizeGuid(trayCtx.grant?.userGuid ?? ""))
                      ?.displayName ?? "this user",
                })}
              </div>
              <div className={styles.revokeConfirmActions}>
                <button
                  type="button"
                  className={styles.btnSecondary}
                  onClick={() => setConfirmingRevoke(false)}
                  disabled={busy}
                >
                  {t("access.oa.cancel")}
                </button>
                <button
                  type="button"
                  className={styles.btnDangerSolid}
                  onClick={handleRevoke}
                  disabled={busy}
                >
                  {t("access.oa.confirmRevoke")}
                </button>
              </div>
            </div>
          )}
        </Tray.Body>

        <Tray.Footer className={styles.trayFooter}>
          {trayCtx?.mode === "edit" && !confirmingRevoke && (
            <button
              type="button"
              className={styles.btnDanger}
              onClick={() => setConfirmingRevoke(true)}
              disabled={busy}
            >
              {t("access.oa.revokeAccess")}
            </button>
          )}
          <div className={styles.trayFooterEnd}>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={closeTray}
              disabled={busy}
            >
              {t("access.oa.cancel")}
            </button>
            {trayCtx?.mode === "add" ? (
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={handleAssign}
                disabled={busy || !pickUserGuid || !pickBundleGuid}
              >
                {isSelfAssignment
                  ? t("access.oa.submitAddSelf")
                  : t("access.oa.submitSendInvitation")}
              </button>
            ) : (
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={handleUpdateBundle}
                disabled={busy || !pickBundleGuid}
              >
                {t("access.oa.submitSave")}
              </button>
            )}
          </div>
        </Tray.Footer>
      </Tray>
    </>
  );
}
